import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { RiAlertLine, RiCloseLine } from 'react-icons/ri';

export interface ConfirmOptions {
    title: string;
    message: string;
    confirmText?: string;
    cancelText?: string;
    variant?: 'danger' | 'warning' | 'info';
}

interface ConfirmDialogProps extends ConfirmOptions {
    isOpen: boolean;
    onConfirm: () => void;
    onCancel: () => void;
}

export default function ConfirmDialog({
    isOpen,
    onConfirm,
    onCancel,
    title,
    message,
    confirmText = "Delete",
    cancelText = "Cancel",
    variant = 'danger'
}: ConfirmDialogProps) {
    return (
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onCancel}
                        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
                    />

                    {/* Dialog */}
                    <motion.div
                        initial={{ scale: 0.95, opacity: 0, y: 20 }}
                        animate={{ scale: 1, opacity: 1, y: 0 }}
                        exit={{ scale: 0.95, opacity: 0, y: 20 }}
                        className="relative w-full max-w-md bg-[#000000] border border-white/10 rounded-xl shadow-2xl overflow-hidden"
                    >
                        {/* Header Stripe based on variant */}
                        <div className={`h-1.5 w-full ${variant === 'danger' ? 'bg-red-500' : 'bg-blue-500'}`} />

                        <div className="p-6">
                            <div className="flex items-start gap-4">
                                <div className={`p-3 rounded-full ${variant === 'danger' ? 'bg-red-500/10 text-red-500' : 'bg-blue-500/10 text-blue-500'}`}>
                                    <RiAlertLine size={24} />
                                </div>
                                <div className="flex-1">
                                    <h3 className="text-xl font-bold text-white mb-2">{title}</h3>
                                    <p className="text-gray-400 leading-relaxed text-sm">
                                        {message}
                                    </p>
                                </div>
                                <button
                                    onClick={onCancel}
                                    className="text-gray-500 hover:text-white transition-colors"
                                >
                                    <RiCloseLine size={24} />
                                </button>
                            </div>

                            <div className="flex items-center justify-end gap-3 mt-8">
                                <button
                                    onClick={onCancel}
                                    className="px-4 py-2 rounded-lg text-sm font-medium text-gray-300 hover:text-white hover:bg-white/5 transition-colors"
                                >
                                    {cancelText}
                                </button>
                                <button
                                    onClick={onConfirm}
                                    className={`
                                        px-6 py-2 rounded-lg text-sm font-bold text-white shadow-lg transition-all
                                        ${variant === 'danger'
                                            ? 'bg-red-600 hover:bg-red-500 shadow-red-500/20'
                                            : 'bg-blue-600 hover:bg-blue-500 shadow-blue-500/20'
                                        }
                                    `}
                                >
                                    {confirmText}
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
}
